import { ArrowRight, Phone, MessageSquare, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import CustomChatWidget from '../components/CustomChatWidget';
import VoiceCallWidget from '../components/VoiceCallWidget';
import TrialSignupModal from '../components/TrialSignupModal';
import { useNavigate } from 'react-router-dom';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const MarketingLandingPage = () => {
  const navigate = useNavigate();
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Trial Signup Modal */}
      <TrialSignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
      {/* Header */}
      <motion.header
        className="fixed w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-gray-900">DoubleMe</span>
            </div>
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/experience')}
                className="text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Experience
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/blog')}
                className="text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Blog
              </button>
              <button
                onClick={() => navigate('/about')}
                className="text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Story
              </button>
              <a
                href="https://app.getadouble.com"
                className="text-gray-600 hover:text-gray-900 font-medium transition"
              >
                Log In
              </a>
              <button
                onClick={() => setIsSignupModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            {/* Early Access Badge */}
            <motion.div variants={fadeIn} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary rounded-full text-dark text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Early Access
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6"
              variants={slideUp}
            >
              Be everywhere at once.
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed"
              variants={slideUp}
            >
              Your AI double answers calls in your voice, with your knowledge, 24/7. Never miss an opportunity again.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20"
              variants={slideUp}
            >
              <button
                onClick={() => setIsSignupModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                <span>Create Your Double</span>
                <span className="text-sm opacity-90">Start for $15/month</span>
              </button>

              <button
                onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white hover:bg-gray-50 text-gray-900 px-8 py-4 rounded-full font-semibold text-lg border-2 border-gray-200 transition"
              >
                Watch 60-Second Demo
              </button>
            </motion.div>
          </motion.div>

          {/* Demo Section - Experience Before Explanation */}
          <motion.div
            id="demo"
            className="max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeIn} className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Don't trust us. Trust your ears.
              </h2>
              <p className="text-xl text-gray-600">
                Call my double right now. 30 seconds. No signup required.
              </p>
            </motion.div>

            <motion.div
              className="flex justify-center"
              variants={stagger}
            >
              {/* Call My Double Card */}
              <motion.div
                variants={slideUp}
                className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-2xl p-8 hover:shadow-xl transition-shadow max-w-2xl w-full"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Call my double</h3>
                </div>

                <div className="h-[400px] flex items-center">
                  <VoiceCallWidget agentId="l9pLCKXTRrvyoWkGMRsK" className="w-full" />
                </div>
              </motion.div>
            </motion.div>

            {/* Post-Demo Revelation */}
            <motion.div
              variants={fadeIn}
              className="mt-12 text-center max-w-3xl mx-auto"
            >
              <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-dark rounded-2xl p-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  That wasn't a recording. That wasn't scripted. That was AI being you, better than you expected.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Your double can do that. All day. Every day. <strong>While you sleep, vacation, live.</strong>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Value Visualization Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={slideUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              One you. Infinite conversations.
            </motion.h2>
            <motion.p variants={slideUp} className="text-xl text-gray-600 max-w-2xl mx-auto">
              While you focus on what matters, your doubles handle everything else.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {/* Scenario Cards */}
            {[
              {
                time: "7:32 AM",
                you: "Morning run with your dog",
                double: "Qualifying three leads from overnight inquiries"
              },
              {
                time: "2:15 PM",
                you: "Deep in strategic planning",
                double: "Handling routine support questions"
              },
              {
                time: "7:45 PM",
                you: "Actually at your kid's recital",
                double: "Booking tomorrow's meetings"
              },
              {
                time: "3:22 AM",
                you: "Sleeping peacefully",
                double: "Closing a deal in Singapore"
              }
            ].map((scenario, index) => (
              <motion.div
                key={index}
                variants={slideUp}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                <div className="text-sm font-mono text-primary mb-4">{scenario.time}</div>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">You:</div>
                    <div className="font-semibold text-gray-900">{scenario.you}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Your Double:</div>
                    <div className="font-semibold text-primary">{scenario.double}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Teaser Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center"
          >
            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-4">
              Start cloning yourself today
            </motion.h2>

            <motion.div variants={slideUp} className="bg-white border-2 border-dark rounded-2xl p-8 max-w-xl mx-auto">
              <div className="mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold text-gray-900">$15</span>
                  <span className="text-2xl text-gray-600">/month</span>
                </div>
                <p className="text-gray-600 mt-2">Less than a single hour of your time</p>
              </div>

              <div className="space-y-3 mb-8 text-left">
                {[
                  "Your voice, perfectly cloned",
                  "Unlimited knowledge uploads",
                  "24/7 availability",
                  "No setup fees, no contracts"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="bg-green-100 rounded-full p-1">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setIsSignupModalOpen(true)}
                className="w-full bg-primary hover:bg-primary/90 text-white px-6 py-4 rounded-lg font-semibold transition mb-4"
              >
                Start Free Trial
                <div className="text-sm opacity-90 mt-1">30 days, no card required</div>
              </button>

              <button
                onClick={() => navigate('/pricing')}
                className="text-primary hover:text-primary/80 font-medium"
              >
                Need more doubles? More minutes? See all options →
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Early Access Section */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center"
          >
            <motion.div variants={fadeIn} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary rounded-full text-dark font-medium">
                <Sparkles className="w-4 h-4" />
                Early Access
              </span>
            </motion.div>

            <motion.h2 variants={slideUp} className="text-4xl font-bold text-gray-900 mb-4">
              You're early to something big
            </motion.h2>

            <motion.p variants={slideUp} className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
              DoubleMe is in early access. Join pioneering professionals who are already multiplying their presence and reclaiming their time.
            </motion.p>

            <motion.div variants={stagger} className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  title: "Shape the product",
                  description: "Your feedback directly influences our roadmap"
                },
                {
                  title: "Lock in founder pricing",
                  description: "Early adopters keep their rates forever"
                },
                {
                  title: "Priority support",
                  description: "Direct access to our founding team"
                }
              ].map((benefit, index) => (
                <motion.div key={index} variants={slideUp} className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.button
              variants={slideUp}
              onClick={() => setIsSignupModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition inline-flex items-center gap-2 mx-auto shadow-lg hover:shadow-xl"
            >
              Join Early Access
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="text-2xl font-bold mb-4">DoubleMe</div>
            <p className="text-gray-400 mb-6">Be everywhere at once.</p>
            <div className="flex justify-center gap-6">
              <button onClick={() => navigate('/pricing')} className="text-gray-400 hover:text-white transition">
                Pricing
              </button>
              <button className="text-gray-400 hover:text-white transition">
                Privacy
              </button>
              <button className="text-gray-400 hover:text-white transition">
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLandingPage;
